import { FC } from "react";
import { Tag } from "antd";
import classNames from "classnames";
import dayjs, { Dayjs } from "dayjs";
import { MdCheck, MdContactMail, MdEmail, MdPhone } from "react-icons/md";
import { useTranslation } from "react-i18next";
import i18n from "src/i18n";
import { transformRecord } from "src/utility/objects";
import { PassengerBasicData, PassengerClassificationType } from "src/Modules/Flights/constants/JourneyDetails";
import { useJourneyDynamicDetails } from "../../../../context";

const formatDate = (v: string | Dayjs) => dayjs(v)?.format("YYYY-M-D");

const PASSENGER_FIELDS_TRANSFORMER = {
  nationality: { get label() { return i18n.t("Flights.details.passengerCard.nationality"); } },
  dob: { get label() { return i18n.t("Flights.details.passengerCard.dateOfBirth"); }, value: formatDate },
  gender: {
    get label() { return i18n.t("Flights.details.passengerCard.gender"); },
    value: (v: number) => (v === 2 ? i18n.t("Flights.details.form.female") : i18n.t("Flights.details.form.male")),
  },
  issueCountry: { get label() { return i18n.t("Flights.details.passengerCard.countryOfIssue"); } },
  passportNumber: { get label() { return i18n.t("Flights.details.passengerCard.passportNumber"); } },
  issueDate: { get label() { return i18n.t("Flights.details.passengerCard.issueDate"); }, value: formatDate },
  expiryDate: { get label() { return i18n.t("Flights.details.passengerCard.expiryDate"); }, value: formatDate },
  birthCertificate: { get label() { return i18n.t("Flights.details.passengerCard.birthCertificate"); } },
};

const PassengerChip: FC<{
  passenger: PassengerBasicData;
  passengerNumber: number;
  isLead: boolean;
  passengerType: PassengerClassificationType;
}> = ({
  passenger,
  passengerNumber,
  isLead,
  passengerType,
}) => {
  const { t } = useTranslation();
  const fullName = [passenger.title, passenger.firstName, passenger.lastName].filter(Boolean).join(" ");
  const filledData = Object.fromEntries(Object.entries(passenger).filter(([, v]) => v != null && v !== ""));
  const fields = transformRecord(filledData, PASSENGER_FIELDS_TRANSFORMER);

  return (
    <div className="outline-border-subtle outline flex w-full flex-col gap-3 rounded-sm p-4 py-6">
      <div className="flex w-full flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div
            className={classNames(
              "flex aspect-square h-10 w-10 items-center justify-center rounded-sm p-2 text-base font-semibold bg-background-primary",
            )}
          >
            <div className={classNames("uppercase text-white")}>P{passengerNumber}</div>
          </div>
          <div className="flex flex-col justify-center">
            <div className="text-font-subtle text-xs">
              {isLead ? t("Flights.details.passengerCard.leadPassenger") : t("Flights.details.passengerCard.passenger")}
            </div>
            <div className="text-font-default -m-0.5 text-base font-medium">{fullName || "—"}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {passengerType && (
            <Tag className="rounded-full! px-3! py-1! text-xs! font-semibold! capitalize!">
              {t(`Flights.details.passengerType.${passengerType.toLowerCase()}`)}
            </Tag>
          )}
          {isLead && (
            <div className="p-1! px-5! rounded-full! flex! items-center! gap-2 text-font-inverted! bg-background-primary text-xs">
              <div><MdCheck className="w-4 h-4" /></div>
              {t("Flights.details.passengerCard.primaryContact")}
            </div>
          )}
        </div>
      </div>
      {fields.length > 0 && (
        <div className="bg-background-minimal flex flex-wrap items-start gap-3 rounded-sm p-3">
          {fields.map(({ key, label, display }) => (
            <div key={key} className="flex min-w-[120px] flex-1 basis-[calc(50%-0.75rem)] flex-col gap-0.5">
              <div className="text-font-subtle text-xs">{label}</div>
              <div className="text-font-default text-sm font-medium">{display}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function PassengerSummaryContent() {
  const { t } = useTranslation();
  const { passengersState } = useJourneyDynamicDetails();
  const [passengers] = passengersState;
  const entries = (passengers.passengers ?? [])
    .map((passenger, index) => ({ passenger, number: index + 1 }))
    .filter(({ passenger }) => passenger.basicData?.firstName || passenger.basicData?.lastName);

  const leadEntry = entries.find(({ passenger }) => passenger.basicData.isLeadPax);
  const otherEntries = entries.filter((entry) => entry !== leadEntry);
  const leadPassenger = leadEntry?.passenger.basicData;

  return (
    <div className="flex w-full flex-col gap-5 p-4 py-6">
      <div className="flex w-full flex-wrap gap-2 md:flex-row flex-col p-2 md:p-0">
        {leadEntry && (
          <div className="w-full">
            <PassengerChip
              passenger={leadEntry.passenger.basicData}
              passengerNumber={leadEntry.number}
              passengerType={leadEntry.passenger.type}
              isLead
            />
          </div>
        )}
        {otherEntries.map(({ passenger, number }) => (
          <div key={number} className="md:w-[calc(50%-0.25rem)] w-full shrink-0 grow-0">
            <PassengerChip passenger={passenger.basicData} passengerNumber={number} passengerType={passenger.type} isLead={false} />
          </div>
        ))}
      </div>

      {leadPassenger && (leadPassenger.email || leadPassenger.phoneNumber) && (
        <>
          <div className="bg-border-subtle h-px w-full" />
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <MdContactMail className="h-5 w-5 text-icon-active" />
              <div className="text-lg font-semibold">{t("Flights.details.contact.contactInformation")}</div>
            </div>
            <div className="bg-background-minimal flex flex-wrap items-start justify-between gap-4 rounded-sm p-4">
              {leadPassenger.email && (
                <div className="flex flex-1 flex-col gap-0.5">
                  <div className="text-font-subtle flex items-center gap-2 text-xs uppercase">
                    <MdEmail /> {t("Flights.details.contact.primaryContactEmail")}
                  </div>
                  <div className="text-font-default truncate text-base font-semibold px-4" title={leadPassenger.email}>
                    {leadPassenger.email}
                  </div>
                </div>
              )}
              {leadPassenger.phoneNumber && (
                <div className="flex flex-1 flex-col gap-0.5">
                  <div className="text-font-subtle flex items-center gap-2 text-xs uppercase">
                    <MdPhone /> {t("Flights.details.contact.mobilePhone")}
                  </div>
                  <div className="text-font-default text-base font-semibold px-4">{leadPassenger.phoneNumber}</div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
